import { Chip, ChipRow, ChipMultiSelect, FilterSection } from '../filters/FilterControls';
import { DEFAULT_PROSPECT_KINDS, PROSPECT_KIND_OPTIONS, REACH_M, activeKinds } from './prospectStyle';

/**
 * Sub-filter for the prospect-reports overlay — admin only, like the overlay.
 *
 * The overlay draws three different KINDS of recovered mark at once: soil
 * samples, drill holes and findings. On a report with a dense recovered grid
 * they overlap heavily, and the question being asked is usually about one of
 * them — "where did the soils actually run", "which holes are on this plate" —
 * so this turns the other two off rather than asking the reader to pick the
 * right mark out of three hundred.
 *
 * Deselecting is not the same as turning the overlay off, and the difference is
 * what `allowEmpty={false}` protects: an overlay that is ticked on but drawing
 * nothing looks like a broken fetch, and there is already a checkbox for "show
 * none of this".
 *
 * The selection also decides what is FETCHED — a category nobody has selected
 * is not requested on pan — so this is a filter in the WFS sense, not just a
 * visibility toggle. See prospectWfs.fetchAll.
 *
 * The second control is about VALUES rather than kinds: a soil sample whose
 * printed number could not be read, and a drill hole with no Au value in the
 * first REACH_M metres — the white disc and the hollow triangle. Both are
 * statements — a sample was taken here, a hole was drilled here, and there is no
 * number the map can show — and on a plate where most of the grid reads that way
 * they crowd out the values that DID survive, which is when this is worth
 * turning on. It is off by default, because a map that quietly drops sampled
 * ground is a map that reads as unsampled.
 *
 * "No value" means no value WITHIN REACH, the same cut the colour uses: a hole
 * assayed only at 88 m has nothing to show a prospector, so it hides alongside
 * one that was never assayed. A hole with gold at 88 m AND 180 ppb at 12 m is
 * not affected — it has a reachable value, is coloured 180 ppb, and stays.
 *
 * The third is depth. A drill mark is coloured by the best value in the first
 * REACH_M metres and nothing below it, because that is the only gold a detector,
 * a scrape or a dryblower can reach — so the deeper intervals are not what the
 * map is showing, and the popup leaves them out unless they are asked for. It
 * always says how many it is holding back: a hole that ends at 30 m on screen
 * must never read as a hole that ended at 30 m in the ground.
 */
export function ProspectFilter({ prospectFilters, setProspectFilters }) {
  const kinds = activeKinds(prospectFilters);
  const hideEmpty = !!prospectFilters?.hideEmpty;
  const showDeep = !!prospectFilters?.showDeep;
  const patch = (p) => setProspectFilters({ ...prospectFilters, ...p });

  return (
    <div>
      <ChipMultiSelect
        label="Show"
        options={PROSPECT_KIND_OPTIONS}
        selected={kinds}
        allowEmpty={false}
        onChange={(next) => setProspectFilters({ ...prospectFilters, kinds: next })}
      />

      {/* Applies to soils and drill holes; a finding carries no assay value to
          be missing, so nothing here changes it. */}
      <FilterSection label="Marks with no value" meta={hideEmpty ? 'hidden' : 'shown'}>
        <ChipRow>
          <Chip
            selected={!hideEmpty}
            onClick={() => patch({ hideEmpty: false })}
            title="Draw samples whose value could not be read, and holes with no Au in the first 30 m"
          >
            Show
          </Chip>
          <Chip
            selected={hideEmpty}
            onClick={() => patch({ hideEmpty: true })}
            title="Draw only marks carrying a gold value the map can show"
          >
            Hide
          </Chip>
        </ChipRow>
      </FilterSection>

      {/* The colour never comes from below this line — only the popup's list of
          intervals is affected. */}
      <FilterSection label={`Intervals below ${REACH_M} m`} meta={showDeep ? 'listed' : 'hidden'}>
        <ChipRow>
          <Chip
            selected={!showDeep}
            onClick={() => patch({ showDeep: false })}
            title={`List only what a prospector can reach — the first ${REACH_M} m`}
          >
            Hide
          </Chip>
          <Chip
            selected={showDeep}
            onClick={() => patch({ showDeep: true })}
            title="List every assayed interval, however deep"
          >
            Show
          </Chip>
        </ChipRow>
      </FilterSection>
    </div>
  );
}

/** Everything on. Exported for the map's initial state. */
export const defaultProspectFilters = () => ({
  kinds: [...DEFAULT_PROSPECT_KINDS],
  // Off: an unread value is still evidence that ground was sampled, and hiding
  // it by default would make sampled ground read as never visited.
  hideEmpty: false,
  // Off: the map describes workable ground, so the popup does too unless asked.
  // What is held back is always counted, never silently dropped.
  showDeep: false,
});

export default ProspectFilter;
